import { api } from "./client";

// ── Типове ────────────────────────────────────────────────────────────────────

export interface CompanyBank {
  id: number;
  bank_name: string;
  bic: string;
  iban: string;
  is_default: boolean;
}

export interface CompanyBankInput {
  bank_name: string;
  bic: string;
  iban: string;
  is_default: boolean;
}

/**
 * „Къде се издават фактурите“ (BE ред в `configuration`, по подразбиране
 * `software`): `software` — както преди фискалната фактура (A4 / ПОС принтер);
 * `fiscal` — САМО фискални фактури от фискалното устройство, всяка софтуерна
 * се отказва; `both` — фискални и софтуерни в ЕДНА поредица. В `fiscal` и
 * `both` номерата на всички фактури дава облакът (обща поредица без дупки).
 */
export type InvoiceIssuingMode = "software" | "fiscal" | "both";

/**
 * Фискалното устройство, закачено за фактурите на фирмата (BE ред
 * `company_invoice_device.<id>`): закача се при първата фактура от апарат,
 * който държи поредицата сам (X/MX серията), и има значение само в `fiscal`.
 */
export interface CompanyInvoiceDevice {
  guid: string;
  serial: string | null;
  name: string;
}

export interface Company {
  id: number;
  name: string;
  object_name: string;
  address: string | null;
  mol: string | null;
  eik: string;
  vat_number: string | null;
  phone: string | null;
  email: string | null;
  separate_invoice_numbering: boolean;
  last_invoice_number: number;
  last_proforma_number: string | null;
  is_default: boolean;
  /** „Печат на фактури“ — POS принтерът за фактурите на фирмата; null —
   *  POS принтерът на оператора. Изтрит принтер идва като null. */
  invoice_pos_device_id: number | null;
  /** „Къде се издават фактурите“. Optional срещу по-старо BE (= `software`). */
  invoice_issuing?: InvoiceIssuingMode;
  /** Закаченото устройство на фактурите; `null` — няма. Пише го само BE. */
  invoice_device?: CompanyInvoiceDevice | null;
  banks: CompanyBank[];
}

// `invoice_device` го няма във входа: закача го само BE (при първата фактура),
// а маха го `clearCompanyInvoiceDevice` — не `PUT` (BE пренебрегва ключа).
export type CompanyPayload = Omit<Company, "id" | "banks" | "invoice_device"> & {
  banks: CompanyBankInput[];
};

/** POS принтер в избора „Печат на фактури“. */
export interface InvoicePrinterOption {
  id: number;
  name: string;
}

// ── HTTP ──────────────────────────────────────────────────────────────────────

// GET /companies — върнатите фирми са подредени: default първа, после по id.
export const getCompanies = async (): Promise<Company[]> => {
  const response = await api.get<Company[]>("/companies");
  return response.data;
};

// GET /companies/default — текуща фирма; 404 ако още не е създавана.
export const getDefaultCompany = async (): Promise<Company> => {
  const response = await api.get<Company>("/companies/default");
  return response.data;
};

// GET /companies/invoice-printers — POS принтерите за „Печат на фактури“
// (`manage_companies`; `GET /devices` иска `manage_devices`).
export const getInvoicePrinters = async (): Promise<InvoicePrinterOption[]> => {
  const response = await api.get<InvoicePrinterOption[]>("/companies/invoice-printers");
  return response.data;
};

// GET /companies/exists — има ли поне една фирма (само влязъл потребител;
// списъкът иска `manage_companies`). Справките крият „Фискални" при false.
export const getCompaniesExist = async (): Promise<boolean> => {
  const response = await api.get<{ exists: boolean }>("/companies/exists");
  return response.data.exists;
};

export const createCompany = async (data: CompanyPayload): Promise<Company> => {
  const response = await api.post<Company>("/companies", data);
  return response.data;
};

export const updateCompany = async (
  id: number,
  data: CompanyPayload,
): Promise<Company> => {
  const response = await api.put<Company>(`/companies/${id}`, data);
  return response.data;
};

// DELETE /companies/{id}/invoice-device — „Освободи устройството“
// (`manage_companies`, без тяло) → фирмата с `invoice_device: null`. В режим
// `fiscal` BE прави номера, който освободеният апарат държи, запазено място;
// без закачено устройство — фирмата без промяна (200).
export const clearCompanyInvoiceDevice = async (id: number): Promise<Company> => {
  const response = await api.delete<Company>(`/companies/${id}/invoice-device`);
  return response.data;
};

export const deleteCompany = async (id: number): Promise<void> => {
  await api.delete(`/companies/${id}`);
};
